import React from 'react';
import OrderDetailsWindow from '../OrderDetails/OrderDetailsWindow';
import { SaveTempDraft } from '../../types/saveTemp';
import { getDefaultDelivery, normalizeDelivery } from './delivery';
import { useOrders, Pedido } from '../../contexts/OrderContext';
import { useMetricsCalculation } from '../OrderDetails';
import { makeChecklistTempPathBuilder } from './tempPathBuilder';
import { sendToTerminal } from '../../lib/terminalLogger';
import { runOrderPreparationPipeline, isPipelineRunning } from '../../lib/corel/pipeline/orderPreparation';
import { clearPreparedPipeline } from '../../lib/corel/pipeline/pipelineKeys';
import { smbCredentialsService, type SmbCredentialFormatted } from '../../services/smbCredentialsService';

type Props = {
  onClose: () => void;
  draft: SaveTempDraft;
};

// Função auxiliar local para normalização básica (apenas para getDimsByService)
function normalizeBasic(value: string): string {
  return String(value || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[^A-Z0-9\s]/g, '')
    .trim();
}

function getDimsByService(type: string): { largura: number | null; altura: number | null; unidade: { umd_id: number; descricao: string; sigla: string } } {
  // Padrão: metros
  const metros = { umd_id: 3, descricao: 'Metros', sigla: 'm' } as const;
  const normalized = normalizeBasic(type);
  
  // Mapear tanto os nomes da API quanto os nomes normalizados
  if (normalized.includes('DTF') && normalized.includes('TEXTIL')) {
    return { largura: 58.5, altura: 205, unidade: metros };
  }
  if (normalized.includes('SUBLIMATICA') || normalized.includes('SUBLIMACAO')) {
    return { largura: 158, altura: 452, unidade: metros };
  }
  if (normalized.includes('DTF') && normalized.includes('UV')) {
    return { largura: 59.5, altura: 102, unidade: metros };
  }
  if (normalized.includes('ADESIVO') && normalized.includes('UV')) {
    return { largura: 106, altura: 101, unidade: metros };
  }
  if (normalized.includes('CAMISETA')) {
    return { largura: null, altura: null, unidade: { umd_id: 4, descricao: 'Unidade', sigla: 'un' } };
  }
  
  return { largura: null, altura: null, unidade: metros };
}

/**
 * ============================================================================
 * 🔧 REGRAS FIXAS TEMP - EDITE AQUI PARA ATUALIZAR VALORES POR CATEGORIA
 * ============================================================================
 * 
 * Esta função retorna valores fixos de regras para o fluxo SAVE TEMP.
 * Essas regras são usadas APENAS quando fluxo.tipo === 'temp' e pro_id === 0.
 * 
 * ⚠️ IMPORTANTE: Essas regras NÃO afetam o fluxo normal de PedidosPage > OrderDetails,
 *    que busca regras da API quando pro_id > 0.
 * 
 * 📝 Para editar valores fixos:
 *    1. Localize a categoria no objeto TEMP_RULES_BY_CATEGORY abaixo
 *    2. Modifique os valores conforme necessário
 *    3. Salve o arquivo - as mudanças serão aplicadas automaticamente
 * 
 * 🔑 Mapeamento de IDs de categoria:
 *    - 1: DTF TÊXTIL / DTF TEXTIL
 *    - 2: DTF UV
 *    - 3: SUBLIMACAO / SUBLIMATICA
 *    - 4: CAMISETA
 * 
 * ⚠️ NOTA SOBRE diretorio_nome:
 *    - A pasta de serviço no Save Temp vem de regras_salvamento.diretorio_nome (backend + fallback local)
 *    - Não derive do nome da categoria (ex.: SUBLIMACAO, não SUBLIMATICA)
 * 
 * 🔐 NOTA SOBRE CREDENCIAIS SMB:
 *    - As credenciais são buscadas via API (/cdr/smb-credential/4)
 *    - NÃO devem ser hardcoded nesta função
 *    - Passadas como parâmetro 'smbCredentials'
 * ============================================================================
 */
function getTempRulesByCategory(
  categoryId: number,
  categoryName: string,
  largura: number | null,
  altura: number | null,
  unidade: { umd_id: number; descricao: string; sigla: string },
  smbCredentials: SmbCredentialFormatted // ✅ Novo parâmetro
): any {
  // ============================================================================
  // 📋 VALORES FIXOS POR CATEGORIA - EDITE ESTES VALORES CONFORME NECESSÁRIO
  // ============================================================================
  const TEMP_RULES_BY_CATEGORY: Record<number, Partial<any>> = {
    1: { // DTF TÊXTIL / DTF TEXTIL
      horario_corte: '16:00',
      // ✅ diretorio_nome removido - será gerado dinamicamente no interceptador usando normalizeFolderUpper()
      exportacao: {
        formato: 'PNG',
        resolucao_dpi: 300,
        cores: 'CMYK',
        compressao: 'PNG',
        sangria_mm: 3,
        icc_profile: 'ISO Coated v2 (ECI)'
      },
      padroes_cdr: {
        versao_arquivo: '20.0',
        perfil_icc: 'ISO Coated v2 (ECI)',
        compactar: true,
        salvar_com_visualizacao: true
      },
      mensagens: {
        hoje: 'Entrega hoje',
        amanha: 'Entrega amanhã',
        fds: 'Entrega segunda-feira',
        redesenho: 'Aguardando redesenho'
      }
    },
    2: { // DTF UV
      horario_corte: '17:00',
      // ✅ diretorio_nome removido - será gerado dinamicamente no interceptador usando normalizeFolderUpper()
      exportacao: {
        formato: 'PNG',
        resolucao_dpi: 300,
        cores: 'CMYK',
        compressao: 'PNG',
        sangria_mm: 3,
        icc_profile: 'ISO Coated v2 (ECI)'
      },
      padroes_cdr: {
        versao_arquivo: '20.0',
        perfil_icc: 'ISO Coated v2 (ECI)',
        compactar: true,
        salvar_com_visualizacao: true
      },
      mensagens: {
        hoje: 'Entrega hoje',
        amanha: 'Entrega amanhã',
        fds: 'Entrega segunda-feira',
        redesenho: 'Aguardando redesenho'
      }
    },
    3: { // SUBLIMACAO / SUBLIMATICA
      horario_corte: '16:00',
      // ✅ diretorio_nome removido - será gerado dinamicamente no interceptador usando normalizeFolderUpper()
      exportacao: {
        formato: 'PNG',
        resolucao_dpi: 300,
        cores: 'CMYK',
        compressao: 'PNG',
        sangria_mm: 3,
        icc_profile: 'ISO Coated v2 (ECI)'
      },
      padroes_cdr: {
        versao_arquivo: '20.0',
        perfil_icc: 'ISO Coated v2 (ECI)',
        compactar: true,
        salvar_com_visualizacao: true
      },
      mensagens: {
        hoje: 'Entrega hoje',
        amanha: 'Entrega amanhã',
        fds: 'Entrega segunda-feira',
        redesenho: 'Aguardando redesenho'
      }
    },
    4: { // CAMISETA
      horario_corte: '16:00',
      // ✅ diretorio_nome removido - será gerado dinamicamente no interceptador usando normalizeFolderUpper()
      exportacao: {
        formato: 'PNG',
        resolucao_dpi: 300,
        cores: 'CMYK',
        compressao: 'PNG',
        sangria_mm: 0,
        icc_profile: 'ISO Coated v2 (ECI)'
      },
      padroes_cdr: {
        versao_arquivo: '20.0',
        perfil_icc: 'ISO Coated v2 (ECI)',
        compactar: true,
        salvar_com_visualizacao: true
      },
      mensagens: {
        hoje: 'Entrega hoje',
        amanha: 'Entrega amanhã',
        fds: 'Entrega segunda-feira',
        redesenho: 'Aguardando redesenho'
      }
    }
  };
  // ============================================================================
  // FIM DO BLOCO EDITÁVEL - Valores acima podem ser modificados manualmente
  // ============================================================================

  // Valores base que são mesclados com os específicos da categoria
  const baseRules: any = {
    pro_id: 0,
    umd_id: unidade.umd_id,
    nome_servico: categoryName,
    descricao: '',
    largura_maxima_cm: largura,
    altura_maxima_cm: altura,
    diretorio_base_interno: '',
    diretorio_base_externo: '',
    diretorio_base_exportpng: '',
    diretorio_base_export_prod: '',
    security: {
      interno: { smb: smbCredentials }, // ✅ Usa credenciais da API
      externo: { smb: smbCredentials }  // ✅ Usa credenciais da API
    },
    seguranca: {
      smb: smbCredentials  // ✅ Usa credenciais da API
    },
    rule_hash: '',
    exportacao: {},
    padroes_cdr: {},
    mensagens: {}
  };

  // Mesclar valores fixos da categoria com valores base
  const categorySpecific = TEMP_RULES_BY_CATEGORY[categoryId] || {};
  
  return {
    ...baseRules,
    ...categorySpecific,
    // Mesclar objetos aninhados corretamente
    exportacao: {
      ...baseRules.exportacao,
      ...(categorySpecific.exportacao || {})
    },
    padroes_cdr: {
      ...baseRules.padroes_cdr,
      ...(categorySpecific.padroes_cdr || {})
    },
    mensagens: {
      ...baseRules.mensagens,
      ...(categorySpecific.mensagens || {})
    }
  };
}

function buildPedidoFromDraft(draft: SaveTempDraft, smbCredentials: SmbCredentialFormatted): Pedido {
  const { largura, altura, unidade } = getDimsByService(draft.prod_cat_item_name);
  const orcId = Number(draft.orderNumber) || Date.now();
  const oitId = Number(draft.orderNumber) || orcId;
  const defaultDelivery = getDefaultDelivery(draft.prod_cat_item_name);
  return {
    orc_id: orcId,
    observacao: null,
    nome_arquivo: draft.baseName,
    valor_total: '0',
    data_entrega: defaultDelivery,
    data_cadastro: null,
    data_emissao: null,
    valor_frete: '0',
    forma_pagamento_id: 0,
    valor_desconto: '0',
    cliente: {
      cli_id: 0,
      nome: draft.clientName,
      razao_social: draft.clientName,
      nome_fantasia: draft.clientName
    },
    status: {
      id_pagamento: 2, // Pago
      descricao: 'Pago',
      descricao_pagamento: 'Pago'
    },
    itens: [
      {
        oit_id: oitId,
        quantidade: 0,
        observacao: '',
        estagio: 'Orçamento',
        valor_unitario: '0',
        valor_total: '0',
        status: { oit_status_id: 2, descricao: 'Em Produção' },
        arquivo: null as any,
        objeto: null as any,
        produto: {
          pro_id: 0,
          nome: draft.prod_cat_item_name,
          tipo: 0,
          categoria: 0,
          quantidade: 1,
          unidade: { ...unidade }
        },
        material: {
          mat_id: 0,
          nome: draft.quality
        },
        // ✅ Usar regras fixas TEMP (valores definidos em getTempRulesByCategory)
        //    Isso garante que todas as categorias tenham regras completas, mesmo sem dimensões
        regras: getTempRulesByCategory(
          draft.prod_cat_item_id,
          draft.prod_cat_item_name,
          largura,
          altura,
          unidade,
          smbCredentials // ✅ Passa credenciais da API
        ) as any
      }
    ]
  } as Pedido;
}

export default function OrderDetailsTempWindow({ onClose, draft }: Props) {
  const { selectPedido, selectedPedido } = useOrders();
  const [targetOrcId] = React.useState(() => Number(draft.orderNumber) || Date.now());
  const [ready, setReady] = React.useState(false);
  const [delivery] = React.useState(getDefaultDelivery(draft.prod_cat_item_name));
  const [pipelineStatus, setPipelineStatus] = React.useState<'idle' | 'running' | 'ok' | 'error'>('idle');
  const [pipelineError, setPipelineError] = React.useState<string | null>(null);
  const [pipelineNonce, setPipelineNonce] = React.useState(0);
  const [prepStep, setPrepStep] = React.useState<'organize' | 'distribute' | 'monitor-start' | 'monitor-sync' | 'done'>('organize');
  
  // ✅ Estado para credenciais SMB
  const [smbCredentials, setSmbCredentials] = React.useState<SmbCredentialFormatted | null>(null);
  const [credentialsLoading, setCredentialsLoading] = React.useState(true);
  const [credentialsError, setCredentialsError] = React.useState<string | null>(null);
  
  // ✅ Estado para controlar se snapshot foi aplicado (similar ao SalesOrdersPage)
  const [snapshotReady, setSnapshotReady] = React.useState(false);
  
  // ✅ PADRÃO SALES: Estados para controle de abertura com validação
  const [showDetails, setShowDetails] = React.useState(false);
  const [pendingOpenDetails, setPendingOpenDetails] = React.useState(false);

  // ✅ Buscar credenciais SMB ao montar o componente
  React.useEffect(() => {
    const loadCredentials = async () => {
      try {
        setCredentialsLoading(true);
        setCredentialsError(null);
        
        // Buscar credencial ID 4 (específica para Save Temp)
        const creds = await smbCredentialsService.getFormattedCredential(4);
        setSmbCredentials(creds);
        
        sendToTerminal('✅ [TEMP] Credenciais SMB carregadas', {
          username: creds.username,
          hasDomain: !!creds.domain
        });
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
        setCredentialsError(errorMsg);
        sendToTerminal('❌ [TEMP] Erro ao carregar credenciais SMB', { error: errorMsg });
        
        // ❌ REMOVIDO: Fallback (viola regra de negócio)
        // Agora o erro é propagado e o usuário deve ser notificado
        // O componente não deve permitir continuar sem credenciais válidas
      } finally {
        setCredentialsLoading(false);
      }
    };

    loadCredentials();
  }, []);

  React.useEffect(() => {
    // Aguardar credenciais antes de construir pedido
    if (!smbCredentials || credentialsLoading) return;
    
    const pedido = buildPedidoFromDraft(draft, smbCredentials);
    sendToTerminal('🔧 [TEMP] Construindo pedido local');
    try { selectPedido(pedido); } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.baseName, draft.orderNumber, smbCredentials, credentialsLoading]);

  React.useEffect(() => {
    if (selectedPedido && Number(selectedPedido.orc_id) === Number(targetOrcId)) {
      setReady(true);
      sendToTerminal('✅ [TEMP] Pedido carregado');
    }
  }, [selectedPedido, targetOrcId]);

  // ✅ PADRÃO SALES: useEffect para abrir OrderDetailsWindow SOMENTE quando context estiver validado
  React.useEffect(() => {
    // Log apenas quando necessário (não a cada mudança de showDetails)
    if (pendingOpenDetails) {
      sendToTerminal('🔍 [TEMP] useEffect monitorando abertura', {
        pendingOpenDetails,
        hasSelectedPedido: !!selectedPedido,
        selectedPedidoId: selectedPedido?.orc_id,
        hasItens: !!selectedPedido?.itens,
        itensLength: selectedPedido?.itens?.length
      });
    }

    if (pendingOpenDetails && selectedPedido) {
      // ✅ Validação adicional: verificar se itens existem
      if (!selectedPedido.itens || selectedPedido.itens.length === 0) {
        sendToTerminal('⚠️ [TEMP] BLOCKED - Pedido sem itens', {
          pedidoId: selectedPedido?.orc_id,
          hasItens: !!selectedPedido?.itens,
          itensLength: selectedPedido?.itens?.length
        });
        return;
      }

      sendToTerminal('✅ [TEMP] Context validado, abrindo OrderDetailsWindow', {
        hasSelectedPedido: true,
        pedidoId: selectedPedido?.orc_id,
        itensCount: selectedPedido.itens.length,
        firstItemId: selectedPedido.itens[0]?.oit_id
      });
      
      // ✅ Pequeno delay para estabilizar antes de abrir (evita race condition com StrictMode)
      setTimeout(() => {
        setShowDetails(true);
        setPendingOpenDetails(false);
      }, 50);
    } else if (pendingOpenDetails && !selectedPedido) {
      sendToTerminal('⏳ [TEMP] AGUARDANDO - selectedPedido ainda não disponível', {
        pendingOpenDetails,
        hasSelectedPedido: false
      });
    }
  }, [selectedPedido, pendingOpenDetails]);

  // Métricas para compor TOTAL no nome final (sempre chamar hook para não quebrar ordem de hooks)
  const isMetros = selectedPedido?.itens?.[0]?.produto?.unidade?.umd_id === 3;
  const selectedObjects = (selectedPedido as any)?.itens?.[0]?.objetos ?? [];
  const { totalObjectsWithQuantity } = useMetricsCalculation({
    selectedObjects,
    metrosVendidos: (selectedPedido as any)?.itens?.[0]?.quantidade ?? 0,
    isMetros: !!isMetros,
    validationMode: 'temp-save',
  });


  const normalizeDeliveryForSave = (value: string) => normalizeDelivery(value as any, draft.prod_cat_item_name);

  // ✅ PADRÃO SALES: Handler para fechar com limpeza completa
  const handleClose = React.useCallback(() => {
    sendToTerminal('🚪 [TEMP] Fechando OrderDetailsWindow', {
      pedidoId: selectedPedido?.orc_id
    });
    
    // 🧹 Limpar snapshot ao fechar (similar ao Sales)
    try {
      const pid = selectedPedido?.orc_id;
      const iid = selectedPedido?.itens?.[0]?.oit_id;
      if (pid && iid) {
        clearPreparedPipeline(pid, iid);
        sessionStorage.removeItem(`corel:dist:v1:${pid}:${iid}`);
        sendToTerminal('🧹 [TEMP] Pipeline limpa ao fechar', { pid, iid });
      }
    } catch {}
    
    // Resetar estados
    setShowDetails(false);
    setSnapshotReady(false);
    setPendingOpenDetails(false);
    
    // Chamar onClose original
    onClose();
  }, [selectedPedido, onClose]);

  // ✅ Callback memorizado para evitar re-renders desnecessários
  const handleSnapshotReady = React.useCallback(() => {
    sendToTerminal('✅ [TEMP] Snapshot ready - fechando overlay de preparação');
    setSnapshotReady(true);
  }, []);

  // ✅ Memorizar o checklistTempPathBuilder para evitar re-criação a cada render
  const memoizedChecklistPathBuilder = React.useMemo(
    () => makeChecklistTempPathBuilder({ draft, totalObjectsWithQuantity }),
    [draft, totalObjectsWithQuantity]
  );

  // ✅ Key estável para o OrderDetailsWindow - evita re-criação do componente pelo StrictMode
  // Usando targetOrcId que é constante durante toda a vida do componente
  const orderDetailsKey = React.useMemo(
    () => `order-details-temp-${targetOrcId}`,
    [targetOrcId]
  );

  // ✅ REMOVIDO: Interceptor de storage.set (era duplicado e causava problemas de timing)
  // O checklistTempPathBuilder já faz todo o trabalho de gerar nome e buscar diretório da API
  // no momento correto (quando o usuário clica em "Salvar" no ChecklistWindow)

  // Mapeamento de erros do pipeline para exibição amigável
  const getErrorDisplay = (errorMessage?: string) => {
    if (!errorMessage) return { title: 'Erro desconhecido', icon: '⚠️' };
    
    // Detecta tipo de erro pelo conteúdo da mensagem
    if (errorMessage.includes('organização de camadas') || errorMessage.includes('organizar camada')) {
      return { 
        title: '❌ Falha ao organizar camadas', 
        detail: errorMessage,
        icon: '🧩',
        hint: 'Verifique se o CorelDRAW está respondendo e se o documento está acessível.'
      };
    }
    if (errorMessage.includes('distribuir') || errorMessage.includes('páginas')) {
      return { 
        title: '❌ Falha ao distribuir páginas', 
        detail: errorMessage,
        icon: '📄',
        hint: 'Certifique-se de que o documento possui objetos válidos para distribuir.'
      };
    }
    if (errorMessage.includes('monitoramento') || errorMessage.includes('monitor')) {
      return { 
        title: '❌ Falha no monitoramento', 
        detail: errorMessage,
        icon: '📡',
        hint: 'O CorelDRAW pode estar ocupado. Tente fechar outras janelas ou documentos.'
      };
    }
    if (errorMessage.includes('snapshot') || errorMessage.includes('objetos')) {
      return { 
        title: '❌ Falha ao capturar objetos', 
        detail: errorMessage,
        icon: '📸',
        hint: 'Verifique se há objetos válidos no documento ou se o CorelDRAW está estável.'
      };
    }
    
    return { 
      title: '❌ Falha ao preparar documento', 
      detail: errorMessage,
      icon: '⚠️',
      hint: 'Tente novamente ou reinicie o CorelDRAW se o problema persistir.'
    };
  };

  // ✅ PADRÃO SALES: Pipeline com sistema de fases
  // FASE_0: Seleção do pedido (já feito em useEffects anteriores)
  // FASE_1: Pipeline de preparação (organizar → distribuir → monitor)
  // FASE_2: Abertura do OrderDetailsWindow (via pendingOpenDetails)
  React.useEffect(() => {
    if (!ready || !selectedPedido) return;
    
    sendToTerminal('━━━━━━ [FASE_0] INÍCIO: Pedido Selecionado ━━━━━━');
    sendToTerminal('🚀 [FASE_0] Pedido pronto para preparação', {
      pedidoId: selectedPedido?.orc_id,
      hasItens: !!selectedPedido?.itens,
      itensCount: selectedPedido?.itens?.length
    });
    
    // ✅ VALIDAÇÃO (similar ao SalesOrdersPage): Verificar se itens existem
    if (!selectedPedido.itens || selectedPedido.itens.length === 0) {
      sendToTerminal('⚠️ [FASE_0] BLOCKED - Pedido sem itens', {
        pedidoId: selectedPedido?.orc_id,
        hasItens: !!selectedPedido?.itens,
        itensLength: selectedPedido?.itens?.length
      });
      setPipelineStatus('error');
      setPipelineError('Pedido não possui itens válidos.');
      return;
    }
    
    sendToTerminal('✅ [FASE_0] Pedido validado');
    sendToTerminal('━━━━━━ [FASE_0] FIM → Iniciando FASE_1 ━━━━━━');
    
    const item = selectedPedido.itens?.[0];
    const pedidoId = selectedPedido.orc_id;
    const itemId = item?.oit_id;
    
    let cancelled = false;
    
    // ✅ TIMEOUT DE SEGURANÇA (similar ao SalesOrdersPage): Fecha overlay após 15s se não receber callback
    const safetyTimeout = setTimeout(() => {
      if (pipelineStatus === 'running') {
        sendToTerminal('⚠️ [TEMP] Timeout 15s - forçando abertura', { pedidoId, itemId });
        setPipelineStatus('ok');
        setPendingOpenDetails(true);
      }
    }, 15000);
    
    (async () => {
      sendToTerminal('🔧 [FASE_1] Iniciando preparação do ambiente...', {
        pedidoId,
        itemId,
        pipelineId: `pipe-${Date.now()}`
      });
      
      // ✅ LIMPAR SNAPSHOT ANTIGO (similar ao SalesOrdersPage)
      try {
        clearPreparedPipeline(pedidoId, itemId);
        sessionStorage.removeItem(`corel:dist:v1:${pedidoId}:${itemId}`);
        sendToTerminal('🧹 [TEMP] Snapshot antigo limpo', { pedidoId, itemId });
      } catch {}
      
      setPipelineStatus('running');
      setPipelineError(null);
      setPrepStep('organize');
      setSnapshotReady(false);
      setShowDetails(false); // ✅ Garantir que está fechado antes de abrir
      
      sendToTerminal('📦 [TEMP] Iniciando preparação via pipeline centralizada', { 
        pedidoId, 
        itemId,
        categoria: draft.prod_cat_item_name,
        draft: { orderNumber: draft.orderNumber, clientName: draft.clientName }
      });
      
      try {
        const result = await runOrderPreparationPipeline({
          pedidoId,
          itemId,
          onProgress: (evt) => {
            try {
              const s = evt?.stage || '';
              if (s.startsWith('organize')) setPrepStep('organize');
              else if (s.startsWith('distribute')) setPrepStep('distribute');
              else if (s.startsWith('monitor:start') || s === 'monitor:ack' || s === 'monitor:ack_error') setPrepStep('monitor-start');
              else if (s.startsWith('monitor:status') || s.startsWith('monitor:sync')) setPrepStep('monitor-sync');
              else if (s === 'end') setPrepStep('done');
            } catch {}
          }
        });
        
        if (cancelled) return;
        
        // ✅ Se pipeline foi bloqueada porque já está rodando, aguardar conclusão
        if (result.status === 'cancelled' && result.error?.code === 'already_running') {
          sendToTerminal('⏭️ [TEMP] Pipeline SKIP - aguardando conclusão da pipeline em execução...');
          
          // Aguardar até 20s pela conclusão da pipeline original
          const maxWait = 20000;
          const checkInterval = 300;
          let waited = 0;
          
          while (waited < maxWait && !cancelled) {
            await new Promise(r => setTimeout(r, checkInterval));
            waited += checkInterval;
            
            // Verificar se a pipeline parou de rodar
            if (!isPipelineRunning()) {
              sendToTerminal('✅ [TEMP] Pipeline original concluiu - prosseguindo', { waited });
              sendToTerminal('━━━━━━ [FASE_1] FIM → Preparando para FASE_2 ━━━━━━');
              setPrepStep('done');
              setPipelineStatus('ok');
              
              // ✅ PADRÃO SALES: Definir pendingOpenDetails para aguardar validação
              sendToTerminal('🎯 [FASE_1] Definindo pendingOpenDetails = true', { pedidoId });
              setPendingOpenDetails(true);
              return;
            }
          }
          
          // Verificação final
          if (!isPipelineRunning()) {
            sendToTerminal('✅ [TEMP] Pipeline concluiu após timeout check', { waited });
            sendToTerminal('━━━━━━ [FASE_1] FIM → Preparando para FASE_2 ━━━━━━');
            setPrepStep('done');
            setPipelineStatus('ok');
            setPendingOpenDetails(true);
            return;
          }
          
          sendToTerminal('⚠️ [TEMP] Timeout aguardando pipeline - permitindo retry', { waited });
          sendToTerminal('━━━━━━ [FASE_1] ERRO - Pipeline não concluiu ━━━━━━');
          setPipelineStatus('error');
          setPipelineError('Pipeline em execução não concluiu a tempo. Tente novamente.');
          return;
        }
        
        if (result.status !== 'success') {
          const errorMsg = result.error?.message || 'Falha ao preparar documento';
          sendToTerminal('💥 [TEMP] Pipeline falhou', { error: errorMsg, code: result.error?.code });
          sendToTerminal('━━━━━━ [FASE_1] ERRO NA PREPARAÇÃO ━━━━━━');
          setPipelineStatus('error');
          setPipelineError(errorMsg);
          return;
        }
        
        setPrepStep('done');
        setPipelineStatus('ok');
        sendToTerminal('✅ [TEMP] Pipeline concluída com sucesso', { 
          pipelineId: result.pipelineId,
          docKey: result.docKey,
          metricsMs: result.metrics
        });
        
        sendToTerminal('━━━━━━ [FASE_1] FIM → Preparando para FASE_2 ━━━━━━');
        sendToTerminal('🚪 [FASE_1] Pedido preparado, aguardando context update', {
          pedidoId,
          willOpenAfterContextUpdate: true
        });
        
        // ✅ PADRÃO SALES: Trigger para abrir após context update (useEffect monitora)
        sendToTerminal('🎯 [FASE_1] Definindo pendingOpenDetails = true', { pedidoId });
        setPendingOpenDetails(true);
        
      } catch (e: any) {
        if (cancelled) return;
        const errorMsg = e?.message || 'Falha no pipeline';
        sendToTerminal('💥 [TEMP] Pipeline falhou com exceção', { 
          error: errorMsg, 
          stack: e?.stack?.substring(0, 200),
          pedidoId,
          itemId
        });
        sendToTerminal('━━━━━━ [FASE_0/FASE_1] ERRO NA PREPARAÇÃO ━━━━━━');
        setPipelineStatus('error');
        setPipelineError(errorMsg);
        
        // 🧹 Limpar flags se falhou (similar ao SalesOrdersPage)
        try {
          clearPreparedPipeline(pedidoId, itemId);
          sessionStorage.removeItem(`corel:dist:v1:${pedidoId}:${itemId}`);
        } catch {}
      } finally {
        clearTimeout(safetyTimeout);
      }
    })();
    
    return () => { 
      cancelled = true; 
      clearTimeout(safetyTimeout);
      sendToTerminal('🧹 [TEMP] Cleanup do useEffect de pipeline', { pedidoId, itemId });
    };
    // ⚠️ CRÍTICO: Usar orc_id em vez de selectedPedido para evitar re-execução
    // quando o objeto muda de referência (mas o ID permanece o mesmo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, selectedPedido?.orc_id, pipelineNonce]);

  return (
    <>
      {/* ✅ Loading de credenciais SMB */}
      {credentialsLoading && (
        <div className="fixed inset-0 bg-navy-900/70 backdrop-blur-[1px] flex items-center justify-center z-[60]">
          <div className="bg-navy-800 border border-navy-700 rounded p-4 w-full max-w-sm text-gray-200">
            <p className="text-sm font-semibold">Carregando credenciais de rede</p>
            <p className="text-xs mt-1 text-gray-400">Aguarde um momento...</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="w-2 h-2 rounded-full border-2 border-yellow-300 border-t-transparent animate-spin"></span>
              <span className="text-xs">Buscando credenciais SMB</span>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Erro ao carregar credenciais */}
      {credentialsError && !credentialsLoading && (
        <div className="fixed inset-0 bg-navy-900/70 backdrop-blur-[1px] flex items-center justify-center z-[60]">
          <div className="bg-navy-800 border border-navy-700 rounded p-4 w-full max-w-sm text-gray-200">
            <p className="text-sm font-semibold text-yellow-300">⚠️ Aviso de Credenciais</p>
            <p className="text-xs mt-1 text-gray-400">
              Não foi possível carregar as credenciais de rede da API.
              Usando credenciais de fallback (emergência).
            </p>
            <p className="text-xs mt-2 text-gray-500">Erro: {credentialsError}</p>
            <div className="flex justify-end gap-2 mt-3">
              <button 
                onClick={onClose} 
                className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancelar
              </button>
              <button 
                onClick={() => {
                  setCredentialsError(null);
                  setReady(true);
                }} 
                className="px-3 py-1.5 text-xs bg-yellow-400 text-navy-900 rounded hover:bg-yellow-300"
              >
                Continuar mesmo assim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ PADRÃO SALES: Renderiza OrderDetailsWindow SOMENTE quando showDetails && selectedPedido */}
      {/* Isso garante que apenas UMA instância seja montada após validação completa */}
      {/* ✅ Key estável evita re-criação do componente pelo StrictMode */}
      {showDetails && selectedPedido && (
        <OrderDetailsWindow 
          key={orderDetailsKey}
          onClose={handleClose}
          onNavigateToMain={onClose}
          pedidoId={String(selectedPedido?.orc_id || '')}
          itemId={String(Number(draft.orderNumber) || 0)}
          normalizeDelivery={normalizeDeliveryForSave}
          checklistMode="temp"
          checklistTempPathBuilder={memoizedChecklistPathBuilder}
          hydrateFromPreparedSnapshot={true}
          hideInternalLoading={!snapshotReady}
          onSnapshotReady={handleSnapshotReady}
        />
      )}
      
      {/* ✅ PADRÃO SALES: Overlay mostra até showDetails + snapshotReady */}
      {/* Condições para mostrar overlay: */}
      {/* 1. Pipeline rodando (pipelineStatus !== 'ok') */}
      {/* 2. Pipeline OK mas showDetails ainda false (aguardando validação) */}
      {/* 3. showDetails true mas snapshot ainda não aplicado */}
      {(pipelineStatus !== 'ok' || !showDetails || (showDetails && !snapshotReady)) && !credentialsLoading && !credentialsError && (
        <div className="fixed inset-0 bg-navy-900/70 backdrop-blur-[1px] flex items-center justify-center z-[60]">
          <div className="bg-navy-800 border border-navy-700 rounded p-4 w-full max-w-sm text-gray-200">
            {pipelineStatus === 'running' || (pipelineStatus === 'ok' && (!showDetails || !snapshotReady)) ? (
              <>
                <p className="text-sm font-semibold">
                  {pipelineStatus === 'ok' && showDetails && !snapshotReady 
                    ? 'Carregando objetos...' 
                    : pipelineStatus === 'ok' && !showDetails
                    ? 'Validando ambiente...'
                    : 'Preparando documento no CorelDRAW'}
                </p>
                <p className="text-xs mt-1 text-gray-400">Este processo leva apenas alguns instantes.</p>
                <ul className="mt-3 space-y-2 text-xs">
                  <li className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${prepStep==='organize' ? 'border-2 border-yellow-300 border-t-transparent animate-spin' : (['distribute','monitor-start','monitor-sync','done'].includes(prepStep) ? 'bg-emerald-400' : 'bg-gray-500/70')}`}></span>
                    <span>Organizando camadas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${prepStep==='distribute' ? 'border-2 border-yellow-300 border-t-transparent animate-spin' : (['monitor-start','monitor-sync','done'].includes(prepStep) ? 'bg-emerald-400' : 'bg-gray-500/70')}`}></span>
                    <span>Distribuindo páginas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${prepStep==='monitor-start' ? 'border-2 border-yellow-300 border-t-transparent animate-spin' : (['monitor-sync','done'].includes(prepStep) ? 'bg-emerald-400' : 'bg-gray-500/70')}`}></span>
                    <span>Ativando monitoramento</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${prepStep==='monitor-sync' ? 'border-2 border-yellow-300 border-t-transparent animate-spin' : (prepStep==='done' ? 'bg-emerald-400' : 'bg-gray-500/70')}`}></span>
                    <span>Sincronizando objetos</span>
                  </li>
                  {/* ✅ NOVO: Step extra para validação do ambiente */}
                  <li className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${(pipelineStatus === 'ok' && !showDetails) ? 'border-2 border-yellow-300 border-t-transparent animate-spin' : (showDetails ? 'bg-emerald-400' : 'bg-gray-500/70')}`}></span>
                    <span>Validando ambiente</span>
                  </li>
                  {/* ✅ Step extra para carregamento da interface */}
                  <li className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${(showDetails && !snapshotReady) ? 'border-2 border-yellow-300 border-t-transparent animate-spin' : (snapshotReady ? 'bg-emerald-400' : 'bg-gray-500/70')}`}></span>
                    <span>Carregando interface</span>
                  </li>
                </ul>
              </>
            ) : (
              <>
                {(() => {
                  const errorDisplay = getErrorDisplay(pipelineError);
                  return (
                    <>
                      <div className="flex items-start gap-3 mb-3">
                        <span className="text-2xl">{errorDisplay.icon}</span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-300">{errorDisplay.title}</p>
                          {errorDisplay.detail && (
                            <p className="text-xs mt-1 text-gray-300">{errorDisplay.detail}</p>
                          )}
                          {errorDisplay.hint && (
                            <p className="text-xs mt-2 text-gray-400 italic">💡 {errorDisplay.hint}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => setPipelineNonce(n => n + 1)} 
                          className="px-3 py-1.5 text-xs bg-yellow-400 text-navy-900 rounded hover:bg-yellow-300 font-medium"
                        >
                          🔄 Tentar novamente
                        </button>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}



